/**
 * Evergreen.jsx
 * The recycling rotation: what comes round again, and when.
 *
 * The screen is arranged around the question people actually have, which is
 * not "what is in the queue" but "what is going out next". So the coming
 * schedule is at the top, gaps are explained rather than left blank, and the
 * queue itself is below it.
 *
 * The setting that decides whether recycled posts arrive as drafts or already
 * scheduled is stated in full rather than hidden behind a toggle label. It is
 * the one control here that can put something live without you looking at it.
 */

import { useCallback, useEffect, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import NoBrands from '../components/NoBrands.jsx';
import { Recycle, Warning, Check, Clock, Plus, Refresh } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function Evergreen() {
  const { brands, loading, error: brandsError } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      setData(await api.evergreen.get(brandId));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  async function act(fn, message) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await fn();
      if (message) setNote(message);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!loading && brands.length === 0) return <NoBrands error={brandsError} />;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Evergreen</div>
          <h1>Posts worth showing again</h1>
          <p>
            Good posts do not stop being good. Add published ones to a rotation, set the times each
            rotation goes out, and Kreeboom brings them back round — resting each one for{' '}
            {data?.limits?.coolingOffDays ?? 21} days, and retiring it when you say.
          </p>
        </div>
        <div className="page-head__actions">
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <button
            className="btn"
            disabled={busy || !data}
            onClick={() => act(() => api.evergreen.run(), 'Rotation filled.')}
          >
            <Refresh /> Fill now
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}
      {note && (
        <div className="notice notice--ok">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {!data ? (
        <div className="card">
          <p className="hint">Loading…</p>
        </div>
      ) : (
        <>
          <Upcoming data={data} />
          <Slots data={data} brandId={brandId} act={act} busy={busy} />
          <Queue data={data} act={act} busy={busy} />
          <HowItPublishes data={data} act={act} busy={busy} />
        </>
      )}
    </>
  );
}

/** What is actually going out, and where the gaps are. */
function Upcoming({ data }) {
  const nothingSetUp = data.slots.length === 0 || data.items.length === 0;

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Clock size={21} />
        </span>
        <div className="card__head-text">
          <h2>Coming up</h2>
          <p>
            The next {data.limits.leadDays} days. These are created a day or more ahead, so there is
            always time to see one and stop it.
          </p>
        </div>
      </div>

      {nothingSetUp ? (
        <p className="hint">
          {data.slots.length === 0
            ? 'No posting times yet — add one below and the rotation starts filling it.'
            : 'No posts in the rotation yet. Add a published post from Drafts or the Calendar.'}
        </p>
      ) : (
        <>
          {data.upcoming.length === 0 && data.gaps.length === 0 && (
            <p className="hint">Every slot in range is already filled.</p>
          )}

          <div className="rows">
            {data.upcoming.map((u) => (
              <div className="row" key={`${u.when}-${u.evergreenId}`}>
                <div>
                  <div className="row__label">{formatLondon(u.when)}</div>
                  <div className="row__sub">
                    {u.category} · {u.caption.slice(0, 80) || 'no caption'}
                  </div>
                </div>
                <div className="row__end">
                  <span className="badge badge--accent">{u.slot}</span>
                </div>
              </div>
            ))}
          </div>

          {data.gaps.length > 0 && (
            <details className="details">
              <summary>
                {data.gaps.length} slot{data.gaps.length === 1 ? '' : 's'} will go unfilled
              </summary>
              <ul className="plainlist">
                {data.gaps.slice(0, 12).map((g) => (
                  <li key={g.key}>
                    <b>{formatLondon(g.when)}</b> — {g.category}: {g.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {data.runs.length > 0 && (
        <details className="details">
          <summary>What recycling has produced so far</summary>
          <ul className="plainlist">
            {data.runs.slice(0, 15).map((r) => (
              <li key={r.id}>
                <b>{formatLondon(r.due_at)}</b> — {r.status ?? 'deleted since'}
                {r.caption ? ` · ${r.caption.slice(0, 70)}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/** When each category goes out. */
function Slots({ data, brandId, act, busy }) {
  const [category, setCategory] = useState('general');
  const [weekday, setWeekday] = useState(1);
  const [time, setTime] = useState('17:00');

  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>Posting times</h2>
          <p>One line per rotation. A category with no times never goes out.</p>
        </div>
      </div>

      {data.slots.length === 0 ? (
        <p className="hint">No times set yet.</p>
      ) : (
        <div className="rows">
          {data.slots.map((s) => (
            <div className="row" key={s.id}>
              <div>
                <div className="row__label">
                  {DAYS[s.weekday]} at {s.time_local}
                </div>
                <div className="row__sub">{s.category}</div>
              </div>
              <div className="row__end">
                <button
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() => act(() => api.evergreen.setSlotEnabled(s.id, !s.enabled))}
                >
                  {s.enabled ? 'Pause' : 'Resume'}
                </button>
                <button
                  className="btn btn--sm btn--danger"
                  disabled={busy}
                  onClick={() => act(() => api.evergreen.removeSlot(s.id))}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="inline-form">
        <div className="field">
          <label htmlFor="evg-cat">Rotation</label>
          <input
            id="evg-cat"
            list="evg-categories"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="tips"
          />
          <datalist id="evg-categories">
            {data.categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>

        <div className="field">
          <label htmlFor="evg-day">Day</label>
          <select id="evg-day" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
            {DAYS.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="evg-time">Time</label>
          <input
            id="evg-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>

        <button
          className="btn btn--primary"
          disabled={busy}
          onClick={() =>
            act(() =>
              api.evergreen.addSlot({ brandId, category, weekday, timeLocal: time }),
            )
          }
        >
          <Plus /> Add
        </button>
      </div>
    </section>
  );
}

/** The queue itself. */
function Queue({ data, act, busy }) {
  const byCategory = new Map();
  for (const item of data.items) {
    if (!byCategory.has(item.category)) byCategory.set(item.category, []);
    byCategory.get(item.category).push(item);
  }

  return (
    <section className="card">
      <div className="card__head">
        <span className="tile">
          <Recycle size={21} />
        </span>
        <div className="card__head-text">
          <h2>In the rotation</h2>
          <p>
            {data.items.length} post{data.items.length === 1 ? '' : 's'}. Removing one here leaves
            the post itself exactly where it is.
          </p>
        </div>
      </div>

      {data.items.length === 0 ? (
        <div className="empty">
          <p>
            Nothing yet. Open a published post from the Calendar or Drafts and choose{' '}
            <b>Add to evergreen</b>.
          </p>
        </div>
      ) : (
        [...byCategory.entries()].map(([category, items]) => (
          <div key={category} className="evg-group">
            <h3 className="evg-group__head">
              {category}
              <span className="muted">
                {items.length} post{items.length === 1 ? '' : 's'}
              </span>
            </h3>

            <div className="rows">
              {items.map((item) => (
                <div className="row" key={item.id}>
                  {/* The queue query returns an id, not the whole media row, so
                      this goes straight to the raw endpoint rather than through
                      MediaPreview, which wants the record. */}
                  {item.media_id && (
                    <img className="row__thumb" src={api.media.rawUrl(item.media_id)} alt="" />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div className="row__label">
                      {(item.caption || 'No caption').slice(0, 90)}
                    </div>
                    <div className="row__sub">
                      {item.state}
                      {item.max_uses ? ` · retires after ${item.max_uses}` : ''}
                      {item.expires_at ? ` · expires ${formatLondon(item.expires_at)}` : ''}
                    </div>
                  </div>
                  <div className="row__end">
                    <button
                      className="btn btn--sm"
                      disabled={busy}
                      onClick={() => act(() => api.evergreen.update(item.id, { paused: !item.paused }))}
                    >
                      {item.paused ? 'Resume' : 'Pause'}
                    </button>
                    <button
                      className="btn btn--sm btn--danger"
                      disabled={busy}
                      onClick={() =>
                        act(
                          () => api.evergreen.remove(item.id),
                          'Taken out of the rotation. The post itself is untouched.',
                        )
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </section>
  );
}

/** The one control here that can put something live unattended. */
function HowItPublishes({ data, act, busy }) {
  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>How recycled posts arrive</h2>
        </div>
      </div>

      <label className="checkline">
        <input
          type="checkbox"
          checked={data.autoSchedule}
          disabled={busy}
          onChange={(e) => act(() => api.evergreen.setAutoSchedule(e.target.checked))}
        />
        <span>
          <b>Schedule them automatically.</b>
          <span className="checkline__hint">
            {data.autoSchedule
              ? 'On. Recycled posts go straight into the scheduler and publish at their slot without ' +
                'being approved again. They are still created at least a day ahead and appear on the ' +
                'Calendar, so you can cancel one.'
              : 'Off. Recycled posts arrive as drafts with their time already filled in, and wait for ' +
                'you to approve them. Safer, and the reason it is the default — a post you approved ' +
                'in March may not be one you would publish today.'}
          </span>
        </span>
      </label>
    </section>
  );
}
